import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAdminGetAdmissionCardQuery } from "../../api/examsApi";

const niqsLogo = "/niqs-logo.png";

function useEmbeddedImage(url: string | null | undefined): string | null {
  const [src, setSrc] = useState<string | null>(url ?? null);
  useEffect(() => {
    if (!url) { setSrc(null); return; }
    setSrc(url);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        setSrc(canvas.toDataURL("image/jpeg"));
      } catch {
        // CORS blocked canvas read — keep direct URL
      }
    };
    img.onerror = () => {};
    img.src = url;
  }, [url]);
  return src;
}

function fmtDate(dateStr: string): { day: string; date: string } {
  if (!dateStr) return { day: "", date: "" };
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return {
    day: d.toLocaleDateString("en-NG", { weekday: "long" }).toUpperCase(),
    date: d.toLocaleDateString("en-NG", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
}

export default function AdminAttendanceCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useAdminGetAdmissionCardQuery(id!);

  const applicant = data?.applicant ?? null;
  const embeddedPhoto = useEmbeddedImage(applicant?.photo_url);

  const session = data?.session ?? null;
  const exam_columns: any[] = Array.isArray(data?.exam_columns)
    ? data.exam_columns
    : [];
  const rawTimetable = data?.timetable;
  const timetable: Record<string, any> =
    rawTimetable &&
    !Array.isArray(rawTimetable) &&
    typeof rawTimetable === "object"
      ? rawTimetable
      : {};
  const sortedDates = Object.keys(timetable).sort();

  // Find the exam column matching this candidate's exam code
  const myColumn = exam_columns.find(
    (c) => c.code === applicant?.exam_code
  );

  const getSubject = (slot: any): string => {
    if (myColumn && slot.subjects?.[myColumn.id]) {
      return slot.subjects[myColumn.id];
    }
    // Fallback: show any non-empty subject
    return (
      Object.values(slot.subjects ?? {})
        .filter(Boolean)
        .join(" / ") || "—"
    );
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; background: white; }
          #attendance-card {
            position: fixed; top: 0; left: 0; width: 148mm;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-slate-200 transition-all"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">
                Attendance Card
              </h1>
              {data && (
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                  {applicant?.name} &nbsp;·&nbsp; {applicant?.exam_no}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => window.print()}
            disabled={isLoading || isError}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            Print Card
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        )}

        {isError && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center">
            <p className="text-rose-700 font-black text-sm uppercase tracking-widest">
              Unable to load card.
            </p>
          </div>
        )}

        {data && applicant && (
          <div className="flex justify-center">
            <div className="shadow-2xl">
              {/* A5 card: 148mm × 210mm */}
              <div
                id="attendance-card"
                className="bg-white border-2 border-slate-900 font-sans"
                style={{
                  width: "148mm",
                  minHeight: "210mm",
                  padding: "8mm",
                  boxSizing: "border-box",
                }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 border-b-2 border-slate-900 pb-3 mb-3">
                  <img
                    src={niqsLogo}
                    alt="NIQS"
                    className="h-14 w-14 object-contain flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="flex-1 text-center">
                    <h1 className="text-sm font-black tracking-wide leading-tight">
                      THE NIGERIAN INSTITUTE OF<br />QUANTITY SURVEYORS
                    </h1>
                    <p className="text-[8px] text-slate-600 mt-0.5">
                      {session?.year ?? ""} Professional Examinations
                    </p>
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-3">
                  <h2 className="text-xs font-black uppercase tracking-widest underline">
                    Examination Attendance Card
                  </h2>
                </div>

                {/* Candidate details + photo */}
                <div className="flex gap-3 mb-4">
                  <div className="flex-1 space-y-1.5">
                    {[
                      { label: "NAME",    value: applicant.name },
                      { label: "EXAM NO", value: applicant.exam_no },
                      { label: "STAGE",   value: applicant.exam_stage },
                      { label: "CENTRE",  value: applicant.center },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="flex items-start gap-2 border-b border-slate-200 pb-0.5"
                      >
                        <span className="text-[8px] font-black uppercase tracking-widest w-16 flex-shrink-0 text-slate-500">
                          {label}:
                        </span>
                        <span className="text-[9px] font-bold text-slate-900">
                          {value || "—"}
                        </span>
                      </div>
                    ))}
                    {applicant.membership_no && (
                      <div className="flex items-start gap-2 border-b border-slate-200 pb-0.5">
                        <span className="text-[8px] font-black uppercase tracking-widest w-16 flex-shrink-0 text-slate-500">
                          MEMB. NO:
                        </span>
                        <span className="text-[9px] font-bold text-slate-900">
                          {applicant.membership_no}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 w-20 h-24 border-2 border-slate-900 overflow-hidden bg-slate-100 flex items-center justify-center">
                    {embeddedPhoto ? (
                      <img
                        src={embeddedPhoto}
                        alt={applicant.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[7px] text-slate-400 text-center px-1">
                        PASSPORT PHOTO
                      </span>
                    )}
                  </div>
                </div>

                {/* Timetable / attendance grid */}
                <div className="mb-4">
                  <table className="w-full border-collapse border border-slate-900 text-[8px]">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-900 px-1.5 py-1 font-black uppercase text-left w-24">
                          Date
                        </th>
                        <th className="border border-slate-900 px-1.5 py-1 font-black uppercase text-left w-20">
                          Time
                        </th>
                        <th className="border border-slate-900 px-1.5 py-1 font-black uppercase text-left">
                          Subject / Paper
                        </th>
                        <th className="border border-slate-900 px-1.5 py-1 font-black uppercase text-center w-20">
                          Invigilator
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDates.map((date) => {
                        const slots = timetable[date] ?? {};
                        const slotKeys = Object.keys(slots);
                        const { day, date: dateLabel } = fmtDate(date);
                        return slotKeys.map((slotKey, slotIdx) => {
                          const slot = slots[slotKey];
                          return (
                            <tr key={`${date}-${slotKey}`}>
                              {slotIdx === 0 && (
                                <td
                                  className="border border-slate-900 px-1.5 py-1 font-black align-top leading-snug"
                                  rowSpan={slotKeys.length}
                                >
                                  <div>{day}</div>
                                  <div className="font-bold">{dateLabel}</div>
                                </td>
                              )}
                              <td className="border border-slate-900 px-1.5 py-1 font-bold whitespace-nowrap">
                                {slot.start_time}–{slot.end_time}
                              </td>
                              <td className="border border-slate-900 px-1.5 py-1 leading-snug">
                                {getSubject(slot)}
                              </td>
                              <td className="border border-slate-900 px-1.5 py-1" />
                            </tr>
                          );
                        });
                      })}
                      {sortedDates.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="border border-slate-900 px-2 py-3 text-center text-slate-400"
                          >
                            Timetable not yet published
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Declaration + signature */}
                <div className="border border-slate-900 p-2 mb-3">
                  <p className="text-[8px] font-bold text-slate-700 mb-4">
                    I hereby confirm my attendance at the examination centre on
                    the date(s) indicated above.
                  </p>
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <div className="border-b border-slate-900 h-6 mb-0.5" />
                      <p className="text-[7px] font-black uppercase tracking-widest text-slate-500">
                        Candidate's Signature
                      </p>
                    </div>
                    <div className="w-28">
                      <div className="border-b border-slate-900 h-6 mb-0.5" />
                      <p className="text-[7px] font-black uppercase tracking-widest text-slate-500">
                        Date
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center">
                  <p className="text-[8px] font-black uppercase tracking-widest border border-slate-900 inline-block px-3 py-0.5">
                    Present this card at the examination centre
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
